/**
 * Cashier Modal Component - Using FormModal with Auto-Save
 */
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const cashierSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(50, 'Nome muito longo'),
  is_active: z.boolean().optional(),
});

export function CashierModal({ open, onOpenChange, cashier, onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!cashier?.id;

  const { register, handleSubmit, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(cashierSchema),
    defaultValues: {
      name: '',
      is_active: true,
    },
  });

  // Reset form when modal opens or cashier changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (cashier) {
      reset({
        name: cashier.name || '',
        is_active: cashier.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        is_active: true,
      });
    }
  }, [open, cashier, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('cashiers.editCashier') : t('cashiers.newCashier')}
      description={isEditing ? 'Atualize os dados do caixa' : 'Preencha os dados do novo caixa'}
      size="sm"
      formId="cashier"
      entityId={cashier?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">{t('cashiers.name')} *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Ex: Caixa 01"
            data-testid="cashier-name-input"
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor="is_active">{t('common.active')}</Label>
            </div>
          )}
        />
      </div>
    </FormModal>
  );
}

export default CashierModal;
